import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CubeIcon,
  DocumentTextIcon,
  FolderIcon,
  MapPinIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL, catalogueAPI, customerPurchaseOrdersAPI, Product, ProductDocument } from '../../services/api';
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  CardBody,
  EmptyState,
  PageHero,
  Spinner,
  Tabs,
} from '../ui';
import {
  flattenProductDocuments,
  getProductDocumentTypeVariant,
  getProductDocumentUrl,
  isPdfProductDocument,
} from '../../utils/productDocuments';
import ProductQuoteHistoryPanel from './ProductQuoteHistoryPanel';

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value?: string) => {
  if (!value) return '-';

  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ProductCataloguePage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedProductId = Number(productId);
  const isDocsRoute = location.pathname.endsWith('/docs');

  const [product, setProduct] = useState<Product | null>(null);
  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProduct = async () => {
      if (!Number.isFinite(parsedProductId)) {
        setError('Invalid product id.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const [productData, productDocuments] = await Promise.all([
          catalogueAPI.getProduct(parsedProductId),
          catalogueAPI.getDocuments(parsedProductId),
        ]);

        setProduct(productData);
        setDocuments(productDocuments);
      } catch (loadError: any) {
        console.error('Error loading product page:', loadError);
        setError(loadError.response?.data?.message || 'Failed to load product.');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [parsedProductId]);

  const allDocuments = useMemo(
    () => flattenProductDocuments(documents),
    [documents],
  );
  const pdfDocuments = useMemo(
    () => allDocuments.filter(isPdfProductDocument),
    [allDocuments],
  );
  const hiddenDocumentsCount = allDocuments.length - pdfDocuments.length;

  const basePath = product ? `/admin/catalogue/${product.id}` : '/admin/catalogue';
  const activeTab = isDocsRoute ? 'docs' : 'details';

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 flex min-h-[50vh] items-center justify-center">
        <div className="flex items-center gap-3 text-neutral-600">
          <Spinner size="lg" />
          <span>Loading product workspace...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/catalogue')}
            leftIcon={<ArrowLeftIcon className="h-4 w-4" />}
          >
            Back to Catalogue
          </Button>
        </div>

        <Card variant="elevated">
          <CardBody className="py-12">
            <EmptyState
              icon={<CubeIcon className="h-12 w-12" />}
              title="Product not available"
              description={error || 'We could not load this product.'}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Breadcrumbs
          items={[
            { label: 'Admin', href: '/admin' },
            { label: 'Catalogue', href: '/admin/catalogue' },
            { label: product.name, href: isDocsRoute ? basePath : undefined },
            ...(isDocsRoute ? [{ label: 'Documents' }] : []),
          ]}
          homeHref="/admin"
        />

        <Button
          variant="outline"
          onClick={() => navigate('/admin/catalogue')}
          leftIcon={<ArrowLeftIcon className="h-4 w-4" />}
        >
          Back to Catalogue
        </Button>
      </div>

      <PageHero
        title={product.name}
        subtitle={`${product.sku} · ${product.category?.name || 'Uncategorized'} · ${product.isActive ? 'Active' : 'Inactive'}`}
        icon={<CubeIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="cyan"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={product.isActive ? 'success' : 'neutral'}>
            {product.isActive ? 'Active Product' : 'Inactive Product'}
          </Badge>
          <Button
            variant={isDocsRoute ? 'outline' : 'primary'}
            size="sm"
            onClick={() => navigate(basePath)}
          >
            Overview
          </Button>
          <Button
            variant={isDocsRoute ? 'primary' : 'outline'}
            size="sm"
            onClick={() => navigate(`${basePath}/docs`)}
          >
            PDF Documents
          </Button>
        </div>
      </PageHero>

      <Tabs
        className="mb-6"
        variant="boxed"
        activeTab={activeTab}
        onChange={(tab) => navigate(tab === 'details' ? basePath : `${basePath}/docs`)}
        tabs={[
          { id: 'details', label: 'Product Overview' },
          { id: 'docs', label: 'PDF Documents', badge: pdfDocuments.length },
        ]}
      />

      {!isDocsRoute ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <Card variant="elevated" className="xl:col-span-2">
              <CardBody className="p-6">
                <div className="mb-6 flex items-center gap-3">
                  <TagIcon className="h-5 w-5 text-cyan-600" />
                  <h2 className="text-lg font-semibold text-neutral-900">Product Snapshot</h2>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">SKU</p>
                    <p className="mt-2 text-lg font-semibold text-neutral-900">{product.sku}</p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">Unit</p>
                    <p className="mt-2 text-lg font-semibold text-neutral-900">{product.unit || 'Not set'}</p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center gap-2">
                      <FolderIcon className="h-4 w-4 text-neutral-500" />
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">Category</p>
                    </div>
                    <p className="mt-2 text-lg font-semibold text-neutral-900">
                      {product.category?.name || 'No category'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className="h-4 w-4 text-neutral-500" />
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">Supplier</p>
                    </div>
                    <p className="mt-2 text-lg font-semibold text-neutral-900">
                      {product.supplier?.name || 'No supplier'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-3xl border border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-sky-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Description</p>
                  <p className="mt-3 text-sm leading-7 text-neutral-700">
                    {product.description || 'No description has been added for this product yet.'}
                  </p>
                </div>
              </CardBody>
            </Card>

            <Card variant="elevated">
              <CardBody className="p-6">
                <div className="mb-5 flex items-center gap-3">
                  <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                  <h2 className="text-lg font-semibold text-neutral-900">Documents Summary</h2>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                    <p className="text-sm text-neutral-500">Total documents</p>
                    <p className="mt-1 text-2xl font-bold text-neutral-900">{allDocuments.length}</p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                    <p className="text-sm text-neutral-500">PDF previews available</p>
                    <p className="mt-1 text-2xl font-bold text-neutral-900">{pdfDocuments.length}</p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                    <p className="text-sm text-neutral-500">Last uploaded</p>
                    <p className="mt-1 text-sm font-semibold text-neutral-900">
                      {allDocuments[0] ? formatDate(allDocuments[0].uploadedAt) : 'No documents yet'}
                    </p>
                  </div>
                </div>

                <Button
                  className="mt-5 w-full"
                  variant="primary"
                  onClick={() => navigate(`${basePath}/docs`)}
                >
                  Open PDF Documents
                </Button>
              </CardBody>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Card variant="elevated">
              <CardBody className="p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <ArchiveBoxIcon className="h-5 w-5 text-cyan-600" />
                    <div>
                      <h2 className="text-lg font-semibold text-neutral-900">Lots & Stock</h2>
                      <p className="mt-1 text-sm text-neutral-500">Lot-backed inventory for this product.</p>
                    </div>
                  </div>
                  <Badge variant={product.stockSummary?.isInventoryLinked === false ? 'neutral' : 'primary'}>
                    {product.stockSummary?.isInventoryLinked === false ? 'Not Tracked' : `${product.stockSummary?.availableQuantity ?? 0} Available`}
                  </Badge>
                </div>

                {!product.lotSummaries || product.lotSummaries.length === 0 ? (
                  <EmptyState
                    icon={<TagIcon className="h-12 w-12" />}
                    title="No lots"
                    description="This product has no lot-backed inventory yet."
                  />
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-neutral-200">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-neutral-500">Lot</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-neutral-500">Available</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-neutral-500">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-neutral-500">Expiry</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-neutral-500">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100 bg-white">
                        {product.lotSummaries.map((lot) => (
                          <tr key={`${lot.lotId || lot.lotNumber}`}>
                            <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                              <div>{lot.lotNumber}</div>
                              {lot.supplier?.name && <div className="mt-1 text-xs text-neutral-500">{lot.supplier.name}</div>}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-900">{lot.availableQuantity} / {lot.totalQuantity}</td>
                            <td className="px-4 py-3 text-sm">
                              <Badge variant={lot.isActive ? 'success' : 'neutral'} size="sm">{lot.isActive ? 'Active' : 'Inactive'}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-600">{lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3 text-sm text-neutral-600">
                              <span className="inline-flex items-center gap-1">
                                <MapPinIcon className="h-4 w-4 text-neutral-400" />
                                {lot.locations?.join(', ') || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card variant="elevated">
              <CardBody className="p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                    <div>
                      <h2 className="text-lg font-semibold text-neutral-900">Customer Purchase Orders</h2>
                      <p className="mt-1 text-sm text-neutral-500">Uploaded customer POs linked to this product through deals.</p>
                    </div>
                  </div>
                  <Badge variant="neutral">{product.customerPurchaseOrders?.length || 0}</Badge>
                </div>

                {!product.customerPurchaseOrders || product.customerPurchaseOrders.length === 0 ? (
                  <EmptyState
                    icon={<DocumentTextIcon className="h-12 w-12" />}
                    title="No customer POs"
                    description="Customer purchase orders will appear here after they are uploaded to deals containing this product."
                  />
                ) : (
                  <div className="space-y-3">
                    {product.customerPurchaseOrders.map((po) => (
                      <div key={po.id} className="rounded-lg border border-neutral-200 bg-white p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-neutral-900">{po.name}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                              <span>{po.deal?.client?.name || 'No client'}</span>
                              <span>{po.deal?.orderNumber || `Deal #${po.dealId}`}</span>
                              <span>{formatFileSize(po.fileSize || 0)}</span>
                              <span>{formatDate(po.uploadedAt)}</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => customerPurchaseOrdersAPI.download(po)}
                            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                          >
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          <Card variant="elevated">
            <CardBody className="p-6">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">Pricing Tiers</h2>
                  <p className="mt-1 text-sm text-neutral-500">Quantity-based pricing for this product.</p>
                </div>
                <Badge variant="neutral">
                  {product.costRanges?.length || 0} tiers
                </Badge>
              </div>

              {product.costRanges && product.costRanges.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                          Min Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                          Max Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                          Unit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                          Price Per Unit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                          Currency
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white">
                      {product.costRanges.map((range) => (
                        <tr key={range.id}>
                          <td className="px-4 py-4 text-sm font-medium text-neutral-900">{range.minQuantity}</td>
                          <td className="px-4 py-4 text-sm text-neutral-600">{range.maxQuantity ?? 'Unlimited'}</td>
                          <td className="px-4 py-4 text-sm text-neutral-600">{range.unit || product.unit || '-'}</td>
                          <td className="px-4 py-4 text-sm text-neutral-900">
                            ${parseFloat(range.pricePerUnit).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-sm text-neutral-600">{range.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  icon={<TagIcon className="h-12 w-12" />}
                  title="No pricing tiers yet"
                  description="This product does not have any cost ranges configured."
                />
              )}
            </CardBody>
          </Card>

          <ProductQuoteHistoryPanel productId={product.id} />

          <Card variant="elevated">
            <CardBody className="p-6">
              <div className="mb-5 flex items-center gap-3">
                <CalendarDaysIcon className="h-5 w-5 text-cyan-600" />
                <h2 className="text-lg font-semibold text-neutral-900">Timeline</h2>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">Created</p>
                  <p className="mt-2 text-sm font-medium text-neutral-900">{formatDate(product.createdAt)}</p>
                </div>
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500">Last updated</p>
                  <p className="mt-2 text-sm font-medium text-neutral-900">{formatDate(product.updatedAt)}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">
          <Card variant="elevated">
            <CardBody className="p-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">Inline PDF Documents</h2>
                  <p className="mt-1 text-sm text-neutral-500">
                    Only PDF files are shown on this page for quick review inside the browser.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="success">{pdfDocuments.length} PDF files</Badge>
                  {hiddenDocumentsCount > 0 && (
                    <Badge variant="neutral">{hiddenDocumentsCount} hidden non-PDF files</Badge>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {pdfDocuments.length === 0 ? (
            <Card variant="elevated">
              <CardBody className="py-12">
                <EmptyState
                  icon={<DocumentTextIcon className="h-12 w-12" />}
                  title="No PDF documents found"
                  description={
                    allDocuments.length > 0
                      ? 'This product has documents, but none of them are PDFs to preview inline.'
                      : 'Upload PDF files like COAs or MSDS sheets to review them here.'
                  }
                />
              </CardBody>
            </Card>
          ) : (
            pdfDocuments.map((document) => (
              <Card key={document.id} variant="elevated">
                <CardBody className="p-0">
                  <div className="border-b border-neutral-200 px-6 py-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900">{document.fileName}</h3>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-neutral-500">
                          <Badge variant={getProductDocumentTypeVariant(document.fileType)}>
                            {document.fileType}
                          </Badge>
                          <span>{formatFileSize(document.fileSize)}</span>
                          <span>{formatDate(document.uploadedAt)}</span>
                        </div>
                      </div>

                      <Link
                        className="text-sm font-medium text-primary-600 hover:text-primary-800"
                        to={getProductDocumentUrl(document)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in new tab
                      </Link>
                    </div>
                  </div>

                  <div className="bg-neutral-100 p-4">
                    <iframe
                      title={`pdf-preview-${document.id}`}
                      src={`${getProductDocumentUrl(document)}#toolbar=0&navpanes=0`}
                      className="h-[80vh] w-full rounded-2xl border border-neutral-200 bg-white"
                    />
                  </div>
                </CardBody>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCataloguePage;
